// Caller-ID bridge: permissions, the on/off switch, and keeping the on-device
// contact cache fed.
//
// The directory is fetched here rather than natively because the WebView already
// holds the auth token — the native side never needs to know about API URLs or
// authentication, only about numbers and names.
import { customersApi } from './api';

const isNative = !!(window as any).Capacitor?.isNativePlatform?.();

export const isCallerIdAvailable = isNative;

export interface CallerIdStatus {
  enabled: boolean;
  phonePermission: boolean;
  overlayPermission: boolean;
  cachedContacts: number;
  /**
   * Holds ROLE_CALL_SCREENING. Without it Android reports a null number while
   * the phone is ringing, so a card can only be shown once the call has ended
   * and the number can be read back from the call log.
   */
  screeningRole: boolean;
}

// Boxed for the same reason as callLog.ts: registerPlugin returns a Proxy that
// turns any property access into a native call, so letting it sit on an await
// path makes the runtime probe `.then` and blow up.
interface PluginBox { p: any }

let pluginPromise: Promise<PluginBox | null> | null = null;

function getPluginBox(): Promise<PluginBox | null> {
  if (!isNative) return Promise.resolve(null);
  if (!pluginPromise) {
    pluginPromise = (async () => {
      try {
        const { registerPlugin } = await import('@capacitor/core');
        return { p: registerPlugin('CallerId') };
      } catch {
        return null;
      }
    })();
  }
  return pluginPromise;
}

const OFFLINE: CallerIdStatus = {
  enabled: false,
  phonePermission: false,
  overlayPermission: false,
  cachedContacts: 0,
  screeningRole: false,
};

export async function getCallerIdStatus(): Promise<CallerIdStatus> {
  const box = await getPluginBox();
  if (!box) return OFFLINE;
  try {
    const res = await box.p.getStatus();
    return {
      enabled: !!res?.enabled,
      phonePermission: !!res?.phonePermission,
      overlayPermission: !!res?.overlayPermission,
      cachedContacts: Number(res?.cachedContacts) || 0,
      screeningRole: !!res?.screeningRole,
    };
  } catch {
    return OFFLINE;
  }
}

/**
 * Opens the system "caller ID app" prompt — the same one Truecaller asks for.
 *
 * This is what makes the card appear *while ringing*. Android stopped putting
 * the caller's number in the PHONE_STATE broadcast for ordinary apps, so
 * without this role the number arrives as null and the card can only be shown
 * after the call ends, from the call log.
 *
 * It cannot be granted silently, and the user can revoke it at any time.
 */
export async function requestScreeningRole(): Promise<boolean> {
  const box = await getPluginBox();
  if (!box) throw new Error('Not running in the Android app');
  const res = await box.p.requestScreeningRole();
  return !!res?.granted;
}

export async function requestPhonePermission(): Promise<boolean> {
  const box = await getPluginBox();
  if (!box) return false;
  try {
    const res = await box.p.requestPhonePermission();
    return !!res?.granted;
  } catch {
    return false;
  }
}

/**
 * Opens the system "draw over other apps" screen. There is no dialog for this
 * permission, so nothing is granted synchronously — re-read getCallerIdStatus()
 * once the user returns to the app.
 */
export async function requestOverlayPermission(): Promise<{ granted: boolean; opened: boolean }> {
  const box = await getPluginBox();
  if (!box) return { granted: false, opened: false };
  try {
    const res = await box.p.requestOverlayPermission();
    return { granted: !!res?.granted, opened: !!res?.opened };
  } catch {
    return { granted: false, opened: false };
  }
}

/**
 * Deliberately does NOT swallow failures. An earlier version returned false on
 * error, so a toggle that never wrote the flag looked identical to one the user
 * had simply not pressed — and the popup silently never fired.
 */
export async function setCallerIdEnabled(enabled: boolean): Promise<boolean> {
  const box = await getPluginBox();
  if (!box) throw new Error('Not running in the Android app');
  const res = await box.p.setEnabled({ enabled });
  return !!res?.enabled;
}

/** Pulls the directory from the server and replaces the on-device cache. */
export async function syncDirectory(): Promise<number> {
  const box = await getPluginBox();
  if (!box) return 0;
  const res = await customersApi.contactDirectory();
  const contacts = res.data || [];
  const stored = await box.p.syncDirectory({ contacts });
  return Number(stored?.stored) || 0;
}

/** Cache-only lookup, for confirming a sync actually landed. */
export async function lookupCached(number: string): Promise<{ found: boolean; person?: string; company?: string }> {
  const box = await getPluginBox();
  if (!box) return { found: false };
  try {
    return await box.p.lookup({ number });
  } catch {
    return { found: false };
  }
}

/** Draws a sample card so the user can verify the overlay without a real call. */
export async function showTestPopup(): Promise<void> {
  const box = await getPluginBox();
  if (!box) throw new Error('Not running in the Android app');
  await box.p.showTest({});
}
